import { useAuth0 } from "@auth0/auth0-react";
import {
  Badge,
  Box,
  chakra,
  Divider,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Switch,
  Text,
} from "@chakra-ui/react";
import { OrderFormElementRoute, OrderStatus } from "enums";
import {
  ChartRouteBaseParams,
  ChartRowElement,
  TriageIVMedicationSection,
} from "modules/charts-shared";
import {
  ButtonCancelOrderModal,
  getOrderDetailsDateShortFormat,
  ORDER_STATUS_COLOR,
} from "modules/orders";
import { useIvStarts, useRefetchIvAndMedication } from "modules/triage/api";
import {
  forwardRef,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { responseToForm } from "modules/triage/utils/iv-medications";
import { useParams } from "react-router-dom";
import {
  Button,
  FormChartOption,
  FormRadioChartOptionGroup,
  Input,
  InputProps,
  Select,
  Textarea,
  UserInfo,
} from "shared";
import { USERID_CLAIMS } from "system-constants";
import { enumMapper } from "utils";
import { getOptions } from "utils/enum-mapper";

type MedsAndIvModalProps = {
  isOpen: boolean;
  onClose(): void;
  type: string;
  item: TriageIVMedicationSection;
  title: string;
  onSave: () => void | Promise<void>;
  isReadOnly?: boolean;
};

type MedicationTypeContentProps = {
  item: TriageIVMedicationSection;
  type: string;
  onClose?: () => void;
  onResetMainForm?: () => Promise<void>;
};

type ItemInfoProps = {
  label: string;
  value: ReactNode;
};

const units = [
  { value: "mg", label: "mg" },
  { value: "mcg", label: "mcg" },
  { value: "mEq", label: "mEq" },
  { value: "iU", label: "iU" },
];

const types = [
  { value: "capsules", label: "Capsules" },
  { value: "tablets", label: "Tablets" },
];

function ItemInfo({ label, value }: ItemInfoProps) {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      py={3}
      px={4}
      alignItems="center"
    >
      <Text color="#9A9EA7">{label}</Text>
      <Text fontWeight="bold">{value}</Text>
    </Box>
  );
}

function ItemDetails({ item }: MedicationTypeContentProps) {
  const { encounterId = "" } = useParams<ChartRouteBaseParams>();

  const { register, setValue, watch } = useFormContext();

  const selectedRoute = watch(`route`);
  const selectedUnit = watch(`unit`);
  const selectedType = watch(`type`);
  const selectedIvStart = watch(`ivStart`);

  const { data: ivStartsData } = useIvStarts(encounterId);
  const ivStarts =
    ivStartsData?.data.map((x, i) => ({
      value: x.id,
      label: `#${i + 1} • ${x.site} • ${x.ga} Ga`,
    })) ?? [];

  const onChangeRoute = (route?: OrderFormElementRoute) => {
    setValue(`route`, route);

    if (route !== "IV") {
      setValue(`stopTime`, null);
      setValue(`amountInfused`, null);
      setValue(`amountWasted`, null);
      setValue(`stoppedBy`, null);
    }
  };

  const isIV = selectedRoute === "IV" || selectedRoute === "IVPB";

  return (
    <Box>
      <Box
        height="310px"
        border="1px solid #FFFFFF"
        borderRadius="10px"
        bgColor="white"
        overflow="hidden"
        minW="544px"
      >
        <ItemInfo
          label="Order Status"
          value={
            <Badge
              bgColor={
                ORDER_STATUS_COLOR[(item.status as OrderStatus) || "InProcess"]
              }
              fontWeight="700"
              fontSize="15px"
              color="white"
              borderRadius="20px"
              padding="0.5rem 1rem"
              textTransform="capitalize"
              width="100%"
              textAlign="center"
            >
              {enumMapper.toDisplay(
                "orderStatus",
                (item.status as OrderStatus) || "InProcess"
              )}
            </Badge>
          }
        />
        <Divider ml="16px" />
        <ItemInfo label="Medication" value={item.medication as string} />
        <Divider ml="16px" />
        <ItemInfo
          label="Dose"
          value={
            typeof item.solution === "string"
              ? item.solution.replace("SNS", "S&S")
              : ""
          }
        />
        <Divider ml="16px" />
        <ItemInfo
          label="Time Ordered"
          value={getOrderDetailsDateShortFormat(item.order?.timeOrdered)}
        />
        <Divider ml="16px" />
        <Flex p={4} alignItems="center">
          <Text color="#9A9EA7">Notes</Text>
          <Text>{item.order?.notes as string}</Text>
        </Flex>
      </Box>

      <Flex columnGap="20px" mt="10px">
        <Box flex={1}>
          <Text color="#9A9EA7" pl={4} pb={1}>
            Route
          </Text>
          <Select<{ value: OrderFormElementRoute; label: string }>
            items={getOptions("orderFormElementRoute")}
            labelAccessor="label"
            valueAccessor="value"
            showBlank
            selectedItem={selectedRoute}
            onSelectedItemChange={(changes) =>
              onChangeRoute(changes.selectedItem?.value)
            }
            isDisabled={
              !["Ordered", "Administered"].includes(item.status as OrderStatus)
            }
          />
        </Box>
      </Flex>

      {selectedRoute === "PO" && (
        <Flex pt={2} gap={2} justifyContent="space-between">
          <Box flex="1 1 0">
            <Text color="#9A9EA7" pl={4} pb={1}>
              Number
            </Text>
            <Input {...register(`number`)} />
          </Box>
          <Box flex="1 1 0">
            <Text color="#9A9EA7" pl={4} pb={1}>
              Amount
            </Text>
            <Input {...register(`amount`)} />
          </Box>
          <Box flex="1 1 0">
            <Text color="#9A9EA7" pl={4} pb={1}>
              Units
            </Text>
            <Select<{ value: string; label: string }>
              items={units}
              labelAccessor="label"
              valueAccessor="value"
              showBlank
              selectedItem={selectedUnit}
              onSelectedItemChange={(changes) =>
                setValue(`unit`, changes.selectedItem?.value)
              }
            />
          </Box>
          <Box flex="1 1 0">
            <Text color="#9A9EA7" pl={4} pb={1}>
              Type
            </Text>
            <Select<{ value: string; label: string }>
              items={types}
              labelAccessor="label"
              valueAccessor="value"
              showBlank
              selectedItem={selectedType}
              onSelectedItemChange={(changes) =>
                setValue(`type`, changes.selectedItem?.value)
              }
            />
          </Box>
        </Flex>
      )}

      {isIV && (
        <Box pt={2}>
          <Text color="#9A9EA7" pl={4} pb={1}>
            IV • Site • Gauge
          </Text>
          <Select<{ value: string; label: string }>
            items={ivStarts}
            labelAccessor="label"
            valueAccessor="value"
            showBlank
            selectedItem={selectedIvStart}
            onSelectedItemChange={(changes) =>
              setValue(`ivStart`, changes.selectedItem?.value)
            }
          />
        </Box>
      )}

      <Box py={4}>
        <Text color="#9A9EA7" pl={4} pb={1}>
          Comments
        </Text>
        <Textarea
          backgroundColor="white"
          borderRadius="10px"
          resize="none"
          height="70px"
          minH="unset"
          border="0"
          {...register(`comments`)}
        />
      </Box>
    </Box>
  );
}

type StepProps = {
  label: string;
  isActive: boolean;
  color: string;
};

function Step({ label, isActive }: StepProps) {
  return (
    <Text
      bgColor={isActive ? "#9A9EA7" : "#D1D1D6"}
      color="white"
      py={1}
      px={2}
      borderRadius="5px"
      fontSize="17px"
      textAlign="center"
      w="100%"
    >
      {label}
    </Text>
  );
}

function MedicationTypeContent({
  item,
  type,
  onClose,
  onResetMainForm,
}: MedicationTypeContentProps) {
  const { control, watch } = useFormContext();
  const status = watch(`status`);

  return (
    <Box display="flex" justifyContent="space-between">
      <ItemDetails item={item} type={type} />
      <Box w="460px">
        <Step
          label="Step 1 - Prepare Medication"
          isActive={status === "Ordered" || status === "Administered"}
          color="cyan"
        />
        <Box py={4} display="flex">
          <Box>
            <Text color="#808080" ml={4}>
              Preparation Time*
            </Text>
            <ChartRowElement
              type="mask-date"
              name={`preparationTime`}
              w="240px"
              isDisabled={status !== "Ordered" && status !== "Administered"}
            />
          </Box>
          <Box ml={4}>
            <Text color="#808080">Prepared by</Text>
            <UserInfo userId={watch(`preparedBy`)} />
          </Box>
        </Box>
        <Step
          label="Step 2 - Start Medication"
          isActive={status === "Prepared" || status === "Administered"}
          color="green"
        />
        <Box py={4} display="flex">
          <Box>
            <Text color="#808080" ml={4}>
              Start Time*
            </Text>
            <ChartRowElement
              type="mask-date"
              name={`startTime`}
              w="240px"
              isDisabled={status !== "Prepared" && status !== "Administered"}
            />
          </Box>
          <Box ml={4}>
            <Text color="#808080">Started by</Text>
            <UserInfo userId={watch(`startedBy`)} />
          </Box>
        </Box>
        <Step
          label="Step 3 - Assess/Administer Medication"
          isActive={status === "Completed" || status === "Administered"}
          color="purple"
        />
        <Box py={4} display="flex" flexDir="column">
          <Box display="flex">
            <Box>
              <Text color="#808080" ml={4}>
                Assessment Time*
              </Text>
              <ChartRowElement
                type="mask-date"
                name={`assessmentTime`}
                w="240px"
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
            </Box>
            <Box ml={4}>
              <Text color="#808080">Assessed by</Text>
              <UserInfo userId={watch(`assessedBy`)} />
            </Box>
          </Box>
          <Box pt={3}>
            <Text color="#808080" ml={4} pb={3}>
              Change*
            </Text>
            <HStack justifyContent="center">
              <FormChartOption
                label="no change"
                control={control}
                name={`noChange`}
                bgColor="white"
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
              <FormChartOption
                label="improved"
                control={control}
                name={`improved`}
                bgColor="white"
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
              <FormChartOption
                label="NAR"
                control={control}
                name={`nar`}
                bgColor="white"
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
              <FormChartOption
                label="continued"
                control={control}
                name={`continued`}
                bgColor="white"
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
            </HStack>
          </Box>
          <Box pt={3}>
            <Text color="#808080" ml={4} pb={3}>
              Pain (0/10)*
            </Text>
            <HStack justifyContent="center">
              <FormRadioChartOptionGroup
                options={[...Array(11).keys()].map((val) => ({
                  label: `${val}`,
                  value: `${val}`,
                }))}
                control={control}
                name={`pain`}
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
            </HStack>
          </Box>

          <Box pt={10} alignSelf={"center"}>
            <ButtonCancelOrderModal
              overrideCancel={true}
              orderId={item.orderId || ""}
              encounterId={item?.order?.encounterId || ""}
              defaultLabel="Request Cancel"
              flex="1"
              sx={{
                borderRadius: "40px",
                width: "255px",
                height: "50px",
                borderWidth: "1px",
                border: "1px solid rgba(255, 59, 48, 1)",
              }}
              onCancelSuccess={async () => {
                setTimeout(async () => {
                  await onResetMainForm?.();
                  onClose?.();
                }, 500);
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function IvStartsTypeContent({
  item,
  type,
  onClose,
  onResetMainForm,
}: MedicationTypeContentProps) {
  const { control, watch } = useFormContext();
  const status = watch(`status`);

  return (
    <Box display="flex" justifyContent="space-between">
      <ItemDetails item={item} type={type} />
      <Box w="460px">
        <Step
          label="Step 1 - Start Medication"
          isActive={status === "Ordered" || status === "Administered"}
          color="green"
        />
        <Box py={4} display="flex">
          <Box>
            <Text color="#808080" ml={4}>
              Start Time*
            </Text>
            <ChartRowElement
              type="mask-date"
              name={`startTime`}
              w="240px"
              isDisabled={status !== "Ordered" && status !== "Administered"}
            />
          </Box>
          <Box ml={4}>
            <Text color="#808080">Started by</Text>
            <UserInfo userId={watch(`startedBy`)} />
          </Box>
        </Box>
        <Step
          label="Step 2 - Assess/Administer Medication"
          isActive={status === "Completed" || status === "Administered"}
          color="purple"
        />
        <Box py={4} display="flex" flexDir="column">
          <Box display="flex">
            <Box>
              <Text color="#808080" ml={4}>
                Assessment Time*
              </Text>
              <ChartRowElement
                type="mask-date"
                name={`assessmentTime`}
                w="240px"
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
            </Box>
            <Box ml={4}>
              <Text color="#808080">Assessed by</Text>
              <UserInfo userId={watch(`assessedBy`)} />
            </Box>
          </Box>
          <Box pt={3}>
            <Text color="#808080" ml={4} pb={2}>
              Change*
            </Text>
            <HStack justifyContent="center">
              <FormChartOption
                label="no change"
                control={control}
                name={`noChange`}
                bgColor="white"
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
              <FormChartOption
                label="improved"
                control={control}
                name={`improved`}
                bgColor="white"
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
              <FormChartOption
                label="NAR"
                control={control}
                name={`nar`}
                bgColor="white"
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
              <FormChartOption
                label="continued"
                control={control}
                name={`continued`}
                bgColor="white"
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
            </HStack>
          </Box>
          <Box pt={3}>
            <Text color="#808080" ml={4} pb={3}>
              Pain (0/10)*
            </Text>
            <HStack justifyContent="center">
              <FormRadioChartOptionGroup
                options={[...Array(11).keys()].map((val) => ({
                  label: `${val}`,
                  value: `${val}`,
                }))}
                control={control}
                name={`pain`}
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
            </HStack>
          </Box>

          <Box pt={10} alignSelf={"center"}>
            <ButtonCancelOrderModal
              overrideCancel={true}
              orderId={item.orderId || ""}
              encounterId={item?.order?.encounterId || ""}
              defaultLabel="Request Cancel"
              flex="1"
              sx={{
                borderRadius: "40px",
                width: "255px",
                height: "50px",
                borderWidth: "1px",
                border: "1px solid rgba(255, 59, 48, 1)",
              }}
              onCancelSuccess={async () => {
                setTimeout(async () => {
                  await onResetMainForm?.();
                  onClose?.();
                }, 500);
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

type SharedInputProps = {
  readOnlyMode?: boolean;
} & InputProps;

const SharedInput = forwardRef<HTMLInputElement, SharedInputProps>(
  ({ readOnlyMode, ...props }, ref) => {
    const readOnlyStyles = readOnlyMode
      ? {
          pointerEvents: "none" as const,
          opacity: 0.6,
          _hover: {},
          _focus: { boxShadow: "none" },
          backgroundColor: "gray.100",
          cursor: "not-allowed",
          isReadOnly: true,
        }
      : {
          backgroundColor: "white",
          cursor: "auto",
        };

    return <Input ref={ref} {...readOnlyStyles} {...props} />;
  }
);

function IvMedicationsTypeContent({
  item,
  type,
  onClose,
  onResetMainForm,
}: MedicationTypeContentProps) {
  const { control, watch, register, setValue } = useFormContext();
  const status = watch(`status`);

  const route = watch(`route`);
  const amountInfused = watch(`amountInfused`);
  const amountWasted = watch(`amountWasted`);
  const isIV = route === "IV" || route === "IVPB";

  const isReadOnlyAmountInputs =
    status !== "InProcess" && status !== "Administered";

  const brandNameMedication = watch(`brandNameMedication`);
  const brandNameMedicationChecked =
    brandNameMedication === true || brandNameMedication === "true";

  return (
    <Box display="flex" justifyContent="start" gap="40px">
      <ItemDetails item={item} type={type} />
      <Box w="460px">
        <Step
          label="Prepare Medication"
          isActive={status === "Administered" || status === "Ordered"}
          color="cyan"
        />
        <Box py={4} display="flex">
          <Box>
            <Text color="#808080" ml={4}>
              Preparation Time*
            </Text>
            <ChartRowElement
              type="mask-date"
              name={`preparationTime`}
              isDisabled={status !== "Administered" && status !== "Ordered"}
              w="240px"
            />
          </Box>
          <Box ml={4}>
            <Text color="#808080">Prepared by</Text>
            <UserInfo userId={watch(`preparedBy`)} />
          </Box>
        </Box>
        <Box
          width="100%"
          height="50px"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={4}
          mb={15}
          backgroundColor="white"
          borderRadius="5px"
        >
          <Text fontSize="16px" color="gray.750" fontWeight="500">
            Brand Name Medication
          </Text>
          <Flex alignItems="center" gap={2}>
            <Text fontSize="16px">
              {brandNameMedicationChecked ? "Yes" : "No"}
            </Text>
            <Switch
              isChecked={brandNameMedicationChecked}
              onChange={(e) => {
                setValue(`brandNameMedication`, e.target.checked);
              }}
              isDisabled={status !== "Administered" && status !== "Ordered"}
              colorScheme="green"
            />
          </Flex>
        </Box>
        <Step
          label="Start Medication"
          isActive={status === "Administered" || status === "Prepared"}
          color="blue"
        />
        <Box py={4} display="flex">
          <Box>
            <Text color="#808080" ml={4}>
              Start Time*
            </Text>
            <ChartRowElement
              type="mask-date"
              name={`startTime`}
              isDisabled={status !== "Administered" && status !== "Prepared"}
              w="240px"
            />
          </Box>
          <Box ml={4}>
            <Text color="#808080">Started by</Text>
            <UserInfo userId={watch(`startedBy`)} />
          </Box>
        </Box>
        {isIV && (
          <>
            <Step
              label="Stop Medication"
              isActive={status === "InProcess" || status === "Administered"}
              color="green"
            />
            <Box py={4} display="flex">
              <Box>
                <Text color="#808080" ml={4}>
                  Stop Time*
                </Text>
                <ChartRowElement
                  type="mask-date"
                  name={`stopTime`}
                  w="240px"
                  isDisabled={
                    status !== "InProcess" && status !== "Administered"
                  }
                />
              </Box>
              <Box ml={4}>
                <Text color="#808080">Stopped by</Text>
                <UserInfo userId={watch(`stoppedBy`)} />
              </Box>
            </Box>
            <Box py={4} display="flex" gap={4}>
              <Box>
                <Text color="#808080" ml={4}>
                  Amount Infused*
                </Text>
                <SharedInput
                  w="220px"
                  border="1px solid"
                  borderColor={amountInfused ? "blue" : "gray.700"}
                  color="blue"
                  {...register("amountInfused")}
                  readOnlyMode={isReadOnlyAmountInputs}
                />
              </Box>
              <Box>
                <Text color="#808080" ml={4}>
                  Amount Wasted*
                </Text>
                <SharedInput
                  w="220px"
                  border="1px solid"
                  borderColor={amountWasted ? "blue" : "gray.700"}
                  color="blue"
                  {...register("amountWasted")}
                  readOnlyMode={isReadOnlyAmountInputs}
                />
              </Box>
            </Box>
          </>
        )}
        <Step
          label="Assess/Administer Medication"
          isActive={status === "Completed" || status === "Administered"}
          color="purple"
        />
        <Box py={4} display="flex" flexDir="column">
          <Box display="flex">
            <Box>
              <Text color="#808080" ml={4}>
                Assessment Time*
              </Text>
              <ChartRowElement
                type="mask-date"
                name={`assessmentTime`}
                w="240px"
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
            </Box>
            <Box ml={4}>
              <Text color="#808080">Assessed by</Text>
              <UserInfo userId={watch(`assessedBy`)} />
            </Box>
          </Box>
          <Box pt={3}>
            <Text color="#808080" ml={4}>
              Change*
            </Text>
            <HStack justifyContent="center">
              <FormChartOption
                label="no change"
                control={control}
                name={`noChange`}
                bgColor="white"
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
              <FormChartOption
                label="improved"
                control={control}
                name={`improved`}
                bgColor="white"
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
              <FormChartOption
                label="NAR"
                control={control}
                name={`nar`}
                bgColor="white"
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
              <FormChartOption
                label="continued"
                control={control}
                name={`continued`}
                bgColor="white"
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
            </HStack>
          </Box>
          <Box pt={3}>
            <Text color="#808080" ml={4}>
              Pain (0/10)*
            </Text>
            <HStack justifyContent="center">
              <FormRadioChartOptionGroup
                options={[...Array(11).keys()].map((val) => ({
                  label: `${val}`,
                  value: `${val}`,
                }))}
                control={control}
                name={`pain`}
                isDisabled={status !== "Completed" && status !== "Administered"}
              />
            </HStack>
          </Box>

          <Box pt={10} alignSelf={"center"}>
            <ButtonCancelOrderModal
              overrideCancel={true}
              orderId={item.orderId || ""}
              encounterId={item?.order?.encounterId || ""}
              defaultLabel="Request Cancel"
              flex="1"
              sx={{
                borderRadius: "40px",
                width: "255px",
                height: "50px",
                borderWidth: "1px",
                border: "1px solid rgba(255, 59, 48, 1)",
              }}
              onCancelSuccess={async () => {
                setTimeout(async () => {
                  await onResetMainForm?.();
                  onClose?.();
                }, 500);
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function MedsAndIvModal({
  isOpen,
  onClose,
  type,
  item,
  onSave,
  isReadOnly,
}: MedsAndIvModalProps) {
  const { getValues, setValue, reset } = useFormContext();
  const { user: currentUser } = useAuth0();
  const userId = currentUser ? currentUser[USERID_CLAIMS] : "";

  const { encounterId = "" } = useParams<ChartRouteBaseParams>();

  const [isLoading, setIsLoading] = useState(false);
  const savingRef = useRef(false);

  const formItem = useMemo(
    () => getValues(`${type}.${item.orderId}`),
    [getValues, type, item.orderId]
  );

  const modalForm = useForm<TriageIVMedicationSection>({
    defaultValues: {},
    shouldUnregister: false,
  });

  const prevFormItemRef = useRef<TriageIVMedicationSection | null>(null);

  useEffect(() => {
    if (isOpen && prevFormItemRef.current !== formItem) {
      const formData = { ...formItem };
      const brandNameMedicationValue =
        formData.brandNameMedication ??
        getValues(`${type}.${item.orderId}.brandNameMedication`);

      formData.brandNameMedication =
        brandNameMedicationValue === "true" ||
        brandNameMedicationValue === true;
      modalForm.reset(formData);
      prevFormItemRef.current = formItem;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, formItem]);

  const { mutateAsync: refetchIvMedication } =
    useRefetchIvAndMedication(encounterId);

  const onResetMainForm = async () => {
    const data = await refetchIvMedication();
    if (data) {
      const formData = responseToForm(data.data);
      reset(formData);
    }
  };

  const reviewMedicationChanges = (formData: TriageIVMedicationSection) => {
    if (formData.preparationTime && formData.status === "Ordered") {
      formData.status = "Prepared";
      if (!formData.preparedBy) {
        formData.preparedBy = userId;
      }
    }

    if (formData.startTime && formData.status === "Prepared") {
      formData.status =
        formData.route === "IV" || formData.route === "IVPB"
          ? "InProcess"
          : "Completed";
      if (!formData.startedBy) {
        formData.startedBy = userId;
      }
    }

    if (
      formData.stopTime &&
      formData.amountInfused &&
      formData.amountWasted &&
      formData.status === "InProcess"
    ) {
      formData.status = "Completed";
      if (!formData.stoppedBy) {
        formData.stoppedBy = userId;
      }
    }

    if (
      formData.assessmentTime &&
      formData.pain &&
      (formData.noChange ||
        formData.improved ||
        formData.nar ||
        formData.continued) &&
      formData.status === "Completed"
    ) {
      formData.status = "Administered";
      if (!formData.assessedBy) {
        formData.assessedBy = userId;
      }
    }

    return formData;
  };

  const handleSave = async () => {
    if (savingRef.current) {
      return;
    }
    savingRef.current = true;
    setIsLoading(true);
    try {
      const formValues = modalForm.getValues();
      const updatedData = reviewMedicationChanges({ ...formValues });

      const brandNameMedicationValue = formValues.brandNameMedication;
      if (brandNameMedicationValue !== undefined) {
        updatedData.brandNameMedication =
          brandNameMedicationValue === true ||
          brandNameMedicationValue === "true"
            ? "true"
            : "false";
      } else {
        const existingValue = getValues(
          `${type}.${item.orderId}.brandNameMedication`
        );
        if (existingValue !== undefined) {
          updatedData.brandNameMedication =
            existingValue === true || existingValue === "true"
              ? "true"
              : "false";
        }
      }

      Object.keys(updatedData).forEach((key) => {
        const fieldPath = `${type}.${item.orderId}.${key}`;
        const value = updatedData[key];
        setValue(fieldPath, value, { shouldDirty: true });
      });

      try {
        await onSave();
        onClose();
      } catch {
        // Parent already toasted API errors
      }
    } finally {
      setIsLoading(false);
      savingRef.current = false;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      closeOnOverlayClick={!isLoading}
      closeOnEsc={!isLoading}
    >
      <ModalContent background="gray.200" minW="1080px">
        <ModalHeader
          display="flex"
          flexDir="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <chakra.span
            fontSize="16px"
            cursor="pointer"
            onClick={isLoading ? undefined : onClose}
            color={isLoading ? "gray.400" : "blue"}
          >
            Cancel
          </chakra.span>
          <chakra.span fontSize="16px">Meds & IV</chakra.span>
          <Button
            variant="label"
            disabled={isReadOnly || isLoading}
            color="blue"
            fontSize="16px"
            onClick={handleSave}
            isLoading={isLoading}
          >
            Save
          </Button>
        </ModalHeader>
        <ModalBody display="flex" flexDir="column" gap="1rem">
          <FormProvider {...modalForm}>
            <chakra.fieldset disabled={isReadOnly}>
              <form>
                <IvMedicationsTypeContent
                  item={item}
                  type={type}
                  onClose={onClose}
                  onResetMainForm={onResetMainForm}
                />
              </form>
            </chakra.fieldset>
          </FormProvider>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export {
  MedsAndIvModal,
  IvMedicationsTypeContent,
  MedicationTypeContent,
  IvStartsTypeContent,
};
